import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  Download,
  Smartphone,
  Shield,
  ArrowLeft,
  Copy,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Reveal from "@/components/Reveal";
import { ANDROID_INSTALL_STEPS, fetchLatestAppRelease, resolveDownloadUrl, safeReleaseNotes } from "@/lib/appDownload";

export default function DownloadApp() {
  const [release, setRelease] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchLatestAppRelease();
        if (!cancelled) {
          setRelease(data);
          setLoadErr("");
        }
      } catch {
        if (!cancelled) {
          setRelease(null);
          setLoadErr("No app build is published yet. Check back soon.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const downloadUrl = resolveDownloadUrl(release);
  const notes = safeReleaseNotes(release?.release_notes);

  async function copyLink() {
    if (!downloadUrl) return;
    try {
      await navigator.clipboard.writeText(downloadUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  }

  return (
    <div className="min-h-screen bg-white" data-testid="download-app-page">
      <Header />

      <main className="pt-8 pb-20">
        <div className="cm-container max-w-5xl">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[14px] font-semibold text-cm-grey hover:text-cm-blue transition-colors mb-8"
          >
            <ArrowLeft size={16} />
            Back to home
          </Link>

          <Reveal variant="fade-up">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-2 bg-cm-lavender text-cm-blue text-[12px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                <Smartphone size={14} />
                Android
              </span>
              {release?.version && (
                <span className="text-[13px] text-cm-grey font-medium">
                  Version {release.version}
                  {release.version_code != null ? ` (${release.version_code})` : ""}
                </span>
              )}
            </div>
            <h1 className="text-[36px] lg:text-[44px] font-bold text-cm-navy tracking-tight leading-[1.15]">
              Download the CoinMBX app
            </h1>
            <p className="mt-4 text-cm-grey text-[16px] leading-[1.75] max-w-2xl">
              Install our Android app from CoinMBX servers. Scan the QR code or tap download below.
            </p>
          </Reveal>

          {loading && (
            <p className="mt-10 text-cm-grey text-[14px]">Loading latest release…</p>
          )}

          {!loading && loadErr && (
            <div
              className="mt-10 flex gap-3 rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-4 text-amber-900"
              data-testid="download-no-release"
            >
              <AlertCircle size={20} className="shrink-0" />
              <p className="text-[14px]">{loadErr}</p>
            </div>
          )}

          {!loading && release && downloadUrl && (
            <>
              {notes ? (
                <p className="mt-6 text-[14px] text-cm-navy bg-cm-lavender/50 rounded-[12px] px-4 py-3 max-w-2xl whitespace-pre-wrap">
                  {notes}
                </p>
              ) : null}

              <div className="mt-12 grid lg:grid-cols-2 gap-10 items-start">
                <Reveal variant="fade-up" delay={100}>
                  <div className="rounded-[20px] border border-[var(--cm-border)] bg-cm-lavender/40 p-8 flex flex-col items-center text-center">
                    <p className="text-[13px] font-bold text-cm-navy uppercase tracking-wider mb-6">
                      Scan to download
                    </p>
                    <div className="bg-white p-4 rounded-[16px] shadow-[0_12px_40px_-16px_rgba(29,45,63,0.2)]">
                      <QRCodeSVG
                        value={downloadUrl}
                        size={200}
                        bgColor="#ffffff"
                        fgColor="#0B1426"
                        level="M"
                      />
                    </div>
                    <p className="mt-6 text-[12px] text-cm-grey break-all max-w-[280px]">
                      {downloadUrl}
                    </p>
                    <button
                      type="button"
                      onClick={copyLink}
                      className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold text-cm-blue hover:underline"
                    >
                      {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                      {copied ? "Link copied" : "Copy download link"}
                    </button>
                  </div>
                </Reveal>

                <Reveal variant="fade-up" delay={180}>
                  <div className="space-y-6">
                    <a
                      href={downloadUrl}
                      className="w-full inline-flex items-center justify-center gap-3 h-[56px] rounded-[14px] bg-cm-blue text-white font-bold text-[17px] shadow-[0_14px_32px_-12px_rgba(2,44,237,0.55)] hover:bg-cm-navy transition-colors"
                      data-testid="download-apk-button"
                    >
                      <Download size={22} />
                      Download APK v{release.version}
                    </a>

                    <p className="text-[13px] text-cm-grey leading-relaxed">
                      Package: <span className="font-mono text-cm-navy">{release.package_name}</span>
                      {release.file_size > 0 && (
                        <> · Size: {(release.file_size / (1024 * 1024)).toFixed(2)} MB</>
                      )}
                    </p>

                    <div className="rounded-[14px] border border-amber-200 bg-amber-50 px-4 py-3 flex gap-3">
                      <Shield size={18} className="text-amber-700 shrink-0 mt-0.5" />
                      <p className="text-[13px] text-amber-900 leading-relaxed">
                        Only install from this official site. You may need to allow installs from your browser.
                      </p>
                    </div>

                    <Link
                      to="/register"
                      className="block text-center text-[14px] font-semibold text-cm-blue hover:underline"
                    >
                      New here? Create an account on web →
                    </Link>
                  </div>
                </Reveal>
              </div>

              <Reveal variant="fade-up" delay={260} className="mt-16">
                <h2 className="text-[22px] font-bold text-cm-navy mb-8">How to install</h2>
                <ol className="grid sm:grid-cols-2 gap-6">
                  {ANDROID_INSTALL_STEPS.map((step) => (
                    <li
                      key={step.n}
                      className="rounded-[16px] border border-[var(--cm-border)] p-6 bg-white"
                    >
                      <span className="inline-flex w-8 h-8 rounded-full bg-cm-blue text-white text-[14px] font-bold items-center justify-center mb-4">
                        {step.n}
                      </span>
                      <h3 className="text-[16px] font-bold text-cm-navy">{step.title}</h3>
                      <p className="mt-2 text-[14px] text-cm-grey leading-[1.7]">{step.body}</p>
                    </li>
                  ))}
                </ol>
              </Reveal>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
